import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, debounce } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { fetchCart, getCartQtyForVariant } from '@theme/utilities';
import { unitsToCases } from '@theme/case-size';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Encapsulates ticket-specific cart-limit state stored in component data attributes.
 */
class TicketProduct {
  static #attributes = [
    'data-is-ticket-product',
    'data-ticket-max-quantity',
    'data-ticket-cart-quantity',
    'data-ticket-limit-error',
  ];

  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
  }

  get isTicket() {
    return this.element.dataset.isTicketProduct === 'true';
  }

  get maxQuantity() {
    const parsed = parseInt(this.element.dataset.ticketMaxQuantity || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  get cartQuantity() {
    const parsed = parseInt(this.element.dataset.ticketCartQuantity || '0', 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  get limitError() {
    return this.element.dataset.ticketLimitError || '';
  }

  /**
   * Returns whether the requested total quantity is allowed for this ticket.
   * Non-ticket products and tickets without a configured maximum are unrestricted.
   * @param {number} quantity
   * @returns {boolean}
   */
  canSetQuantity(quantity) {
    const maxQuantity = this.maxQuantity;
    return !this.isTicket || maxQuantity === null || quantity <= maxQuantity;
  }

  /**
   * Returns whether a quantity can be added to the current ticket quantity.
   * @param {number} quantity
   * @returns {boolean}
   */
  canAddQuantity(quantity) {
    return this.canSetQuantity(this.cartQuantity + quantity);
  }

  /**
   * Synchronizes the ticket quantity with the authoritative cart state.
   * @param {number} quantity
   */
  updateCartQuantity(quantity) {
    if (this.isTicket) {
      this.element.dataset.ticketCartQuantity = quantity.toString();
    }
  }

  /**
   * Copies ticket state from a server-rendered component after a variant change.
   * @param {HTMLElement | null} source
   */
  syncFrom(source) {
    if (!source) return;

    for (const attribute of TicketProduct.#attributes) {
      const value = source.getAttribute(attribute);
      if (value === null) {
        this.element.removeAttribute(attribute);
      } else {
        this.element.setAttribute(attribute, value);
      }
    }
  }
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * When rendered inside a product page form (data-in-product-page-form="true"), it also
 * manages an inline quantity selector that replaces the button when the variant is in cart.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @property {HTMLElement | undefined} buttonWrapper - Wrapper div around the add to cart button.
 * @property {HTMLElement | undefined} inCartControls - The in-cart quantity controls container.
 * @property {HTMLSpanElement | undefined} inCartCount - The quantity count display.
 * @property {HTMLButtonElement | undefined} inCartMinus - The minus/remove button.
 * @property {HTMLButtonElement | undefined} inCartPlus - The plus button.
 * @property {HTMLElement | undefined} inCartError - The in-cart error container element.
 * @property {HTMLElement | undefined} inCartErrorText - The in-cart error text element.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  #ticketProduct = new TicketProduct(this);

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {number | undefined} */
  #inCartErrorTimeout;

  #inCartAbortController = new AbortController();

  /**
   * Optimistic quantity shown to the user while a debounced request is pending.
   * `undefined` means no pending change — the source of truth is `dataset.cartQuantity`.
   * @type {number | undefined}
   */
  #pendingQuantity;

  /**
   * Last quantity confirmed by the server, used to roll back on error.
   * @type {number | undefined}
   */
  #lastConfirmedQuantity;

  /**
   * Debounced wrapper that fires `#changeCartQuantity` after the user stops clicking.
   * Using an arrow function so `this` is captured at class-field initialisation time.
   */
  #debouncedSyncCart = debounce(
    /** @param {string} variantId @param {number} newQuantity */
    (variantId, newQuantity) => this.#changeCartQuantity(variantId, newQuantity),
    400
  );

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);

    if (this.dataset.inProductPageForm !== 'true') return;

    const cartQty = parseInt(this.dataset.cartQuantity || '0');
    this.#renderInCartState(cartQty);

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#inCartAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    if (this.#inCartErrorTimeout) clearTimeout(this.#inCartErrorTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
    this.#debouncedSyncCart.cancel();
    this.#inCartAbortController.abort();
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Updates the in-cart quantity display and toggles between button and in-cart controls.
   * Called externally by ProductFormComponent when the cart changes.
   * @param {number} cartQty - The current cart quantity for this variant.
   */
  updateInCartState(cartQty) {
    if (this.dataset.inProductPageForm !== 'true') return;
    this.#renderInCartState(cartQty);
  }

  /**
   * Syncs ticket state after the selected variant changes.
   * @param {HTMLElement | null} source
   */
  syncTicketProduct(source) {
    this.#ticketProduct.syncFrom(source);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    const requestedQuantity =
      Number(new FormData(form).get('quantity')) || Number(productForm?.dataset.quantityDefault) || 1;
    if (!productForm?.canAddTicketQuantity(requestedQuantity)) return;

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  /**
   * The case size for case-pack products (erp.case_size), 1 for regular products.
   * The cart quantity is always stored in units; case products step by a full case.
   * @returns {number}
   */
  get caseSize() {
    const parsed = parseInt(this.dataset.caseSize || '1', 10);
    return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
  }

  /**
   * Increases the cart quantity by one selling unit (1 item, or 1 case for
   * case-pack products) when already in cart (product page form only).
   * Updates the display immediately (optimistic) and schedules a debounced server sync.
   * @param {Event} event
   */
  handleInCartIncrease(event) {
    event.preventDefault();

    const variantId = this.#getVariantId();
    if (!variantId) return;

    const caseSize = this.caseSize;
    const currentQty = this.#getPendingOrConfirmedQty();
    // Snap up to the next full multiple so misaligned quantities self-heal
    const newQty = (Math.floor(currentQty / caseSize) + 1) * caseSize;
    if (!this.#ticketProduct.canSetQuantity(newQty)) {
      this.#showInCartError(this.#ticketProduct.limitError);
      this.#renderInCartState(currentQty);
      return;
    }

    this.#applyOptimisticUpdate(variantId, newQty);
  }

  /**
   * Decreases the cart quantity by one selling unit (1 item, or 1 case for
   * case-pack products), removing the item when the last unit is left (product page form only).
   * Updates the display immediately (optimistic) and schedules a debounced server sync.
   * @param {Event} event
   */
  handleInCartDecrease(event) {
    event.preventDefault();

    const variantId = this.#getVariantId();
    if (!variantId) return;

    const caseSize = this.caseSize;
    const currentQty = this.#getPendingOrConfirmedQty();
    // Snap down to the previous full multiple so misaligned quantities self-heal
    const newQty = Math.max(0, (Math.ceil(currentQty / caseSize) - 1) * caseSize);
    this.#applyOptimisticUpdate(variantId, newQty);
  }

  /**
   * Returns the current pending quantity (if clicks are buffered) or the last
   * server-confirmed quantity stored in the dataset attribute.
   * @returns {number}
   */
  #getPendingOrConfirmedQty() {
    return this.#pendingQuantity ?? parseInt(this.dataset.cartQuantity || '0');
  }

  /**
   * Applies an optimistic quantity update: updates the UI immediately, saves the
   * "last confirmed" baseline for potential rollback, then re-schedules the debounced request.
   * @param {string} variantId
   * @param {number} newQty
   */
  #applyOptimisticUpdate(variantId, newQty) {
    // Remember the baseline only at the start of a new burst (when nothing is pending yet).
    if (this.#pendingQuantity === undefined) {
      this.#lastConfirmedQuantity = parseInt(this.dataset.cartQuantity || '0');
    }

    this.#pendingQuantity = newQty;
    this.#renderInCartState(newQty);
    this.#debouncedSyncCart(variantId, newQty);
  }

  /** @returns {string | undefined} */
  #getVariantId() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.closest('form')?.querySelector('input[name="id"]')
    );
    return variantIdInput?.value;
  }

  /**
   * @param {number} cartQty - The cart quantity in units.
   */
  #renderInCartState(cartQty) {
    const { buttonWrapper, inCartControls, inCartCount, inCartMinus } = this.refs;

    if (!buttonWrapper || !inCartControls) return;

    this.dataset.cartQuantity = cartQty.toString();

    const caseSize = this.caseSize;
    // Case products display the count in cases, not units
    const displayQty = caseSize > 1 ? Math.max(1, unitsToCases(cartQty, caseSize)) : cartQty;

    const isInCart = cartQty > 0;
    buttonWrapper.hidden = isInCart;
    inCartControls.hidden = !isInCart;

    if (inCartCount) {
      inCartCount.textContent = displayQty.toString();
    }

    if (inCartMinus) {
      inCartMinus.dataset.isLastItem = (cartQty <= caseSize).toString();
    }
  }

  /**
   * Shows an inline error message below the in-cart controls and auto-hides it.
   * @param {string} message
   */
  #showInCartError(message) {
    const { inCartError, inCartErrorText } = this.refs;
    if (!inCartError || !inCartErrorText) return;

    if (this.#inCartErrorTimeout) clearTimeout(this.#inCartErrorTimeout);

    inCartErrorText.textContent = message;
    inCartError.classList.remove('hidden');

    this.#inCartErrorTimeout = setTimeout(() => {
      inCartError.classList.add('hidden');
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Calls /cart/change.js to set an absolute quantity for the variant.
   * On success, reconciles the display with the server-confirmed quantity.
   * On error, rolls the display back to the last server-confirmed quantity.
   * @param {string} variantId
   * @param {number} newQuantity
   */
  async #changeCartQuantity(variantId, newQuantity) {
    const { inCartMinus, inCartPlus } = this.refs;

    if (inCartMinus) inCartMinus.disabled = true;
    if (inCartPlus) inCartPlus.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: newQuantity }),
      });

      const cart = /** @type {Cart & {errors?: string}} */ (await response.json());

      if (cart.errors) {
        this.#showInCartError(cart.errors);
        this.#rollbackOptimisticUpdate();
        return;
      }

      const actualQty = getCartQtyForVariant(variantId, cart);
      this.#pendingQuantity = undefined;
      this.#lastConfirmedQuantity = actualQty;
      this.#renderInCartState(actualQty);

      this.dispatchEvent(
        new CartUpdateEvent(cart, variantId, {
          source: 'add-to-cart-in-cart',
          variantId,
        })
      );
    } catch (error) {
      console.error('Cart update failed:', error);
      this.#rollbackOptimisticUpdate();
    } finally {
      if (inCartMinus) inCartMinus.disabled = false;
      if (inCartPlus) inCartPlus.disabled = false;
    }
  }

  /**
   * Reverts the display to the last server-confirmed quantity after a failed request.
   */
  #rollbackOptimisticUpdate() {
    const rollbackQty = this.#lastConfirmedQuantity ?? 0;
    this.#pendingQuantity = undefined;
    this.#renderInCartState(rollbackQty);
  }

  /**
   * Listens for cart updates from other parts of the page (e.g. cart drawer)
   * and syncs the in-cart state. Any pending optimistic update is discarded
   * so the external (server-authoritative) state takes precedence.
   * @param {CartUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (event.detail?.data?.source === 'add-to-cart-in-cart') return;

    // Discard pending optimistic burst — external update is the source of truth.
    this.#debouncedSyncCart.cancel();
    this.#pendingQuantity = undefined;

    const variantId = this.#getVariantId();
    if (!variantId) return;

    const cart = /** @type {Cart} */ (event.detail?.resource);

    if (cart?.items) {
      this.#renderInCartState(getCartQtyForVariant(variantId, cart));
      return;
    }

    try {
      const response = await fetch('/cart.js');
      const cartData = /** @type {Cart} */ (await response.json());
      this.#renderInCartState(getCartQtyForVariant(variantId, cartData));
    } catch {
      console.error('Failed to fetch cart data');
    }
  };

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>, total_price: number}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #ticketProduct = new TicketProduct(this);

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateInbond, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * The case size for case-pack products (erp.case_size), 1 for regular products.
   * @returns {number}
   */
  get caseSize() {
    const parsed = parseInt(this.dataset.caseSize || '1', 10);
    return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
  }

  /**
   * Returns whether the requested ticket quantity can be added to the cart.
   * @param {number} quantity
   * @returns {boolean}
   */
  canAddTicketQuantity(quantity) {
    return this.#ticketProduct.canAddQuantity(quantity);
  }

  /**
   * Shows a validation error and temporarily disables all add-to-cart buttons.
   * @param {string} errorMessage
   * @param {NodeListOf<AddToCartComponent>} addToCartContainers
   * @param {HTMLElement | undefined} errorElement
   */
  #blockSubmission(errorMessage, addToCartContainers, errorElement) {
    for (const container of addToCartContainers) {
      container.disable();
    }

    if (errorElement) {
      errorElement.classList.remove('hidden');

      const textNode = errorElement.childNodes[2];
      if (textNode) {
        textNode.textContent = errorMessage;
      } else {
        errorElement.appendChild(document.createTextNode(errorMessage));
      }

      this.#setLiveRegionText(errorMessage);

      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        errorElement.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }

    setTimeout(() => {
      for (const container of addToCartContainers) {
        container.enable();
      }
    }, ERROR_BUTTON_REENABLE_DELAY);
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity (in units) for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartQty = getCartQtyForVariant(variantIdInput.value, cart);
    this.#ticketProduct.updateCartQuantity(cartQty);

    // The quantity selector of case products operates in cases, not units
    const selectorQty = unitsToCases(cartQty, this.caseSize);

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(selectorQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(selectorQty);

    // Sync in-cart controls on the add-to-cart button (works in units)
    this.refs.addToCartButtonContainer?.updateInCartState(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    // Case-pack forms already carry unit quantities via a hidden input which the
    // quantity selector keeps in sync, so FormData needs no conversion here.
    const formData = new FormData(form);
    const requestedQuantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

    if (!this.#ticketProduct.canAddQuantity(requestedQuantity)) {
      this.#blockSubmission(this.#ticketProduct.limitError, allAddToCartContainers, addToCartTextError);
      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    /** @type {AddToCartComponent[]} */
    const temporarilyDisabledContainers = [];
    for (const container of allAddToCartContainers) {
      const button = container.refs.addToCartButton;
      if (button && !button.disabled) {
        container.disable();
        temporarilyDisabledContainers.push(container);
      }
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: unitsToCases(
                Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                this.caseSize
              ),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: unitsToCases(
                Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
                this.caseSize
              ),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        for (const container of temporarilyDisabledContainers) {
          container.enable();
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID and ticket state.
    variantId.value = event.detail.resource?.id ?? '';
    const newProductForm = /** @type {HTMLElement | null} */ (
      event.detail.data.html.querySelector('product-form-component')
    );
    this.#ticketProduct.syncFrom(newProductForm);

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Sync in-cart state for the new variant using server-rendered cart quantity
    if (currentAddToCartButtonContainer.dataset.inProductPageForm === 'true') {
      const newAtcComponent = /** @type {HTMLElement | null} */ (
        event.detail.data.html.querySelector('add-to-cart-component[data-in-product-page-form]')
      );
      currentAddToCartButtonContainer.syncTicketProduct(newAtcComponent);

      const newCartQty = parseInt(newAtcComponent?.dataset.cartQuantity || '0');
      currentAddToCartButtonContainer.updateInCartState(newCartQty);
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  #onVariantUpdateInbond = (event) => {
    if (!event.detail.data.newProduct && event.detail.data.productId !== this.dataset.productId) return;

    const form = this.querySelector('form');
    if (!form) return;

    const currentInput = form.querySelector('input[data-inbond]');
    const newInput = event.detail.data.html?.querySelector('input[data-inbond]');

    if (newInput && !currentInput) {
      form.appendChild(newInput.cloneNode(true));
    } else if (!newInput && currentInput) {
      currentInput.remove();
    } else if (newInput && currentInput) {
      currentInput.name = newInput.name;
      currentInput.value = newInput.value;
    }
  };

  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

/**
 * A custom element that manages the buy-N-for-free-shipping button.
 *
 * @typedef {object} FreeShippingButtonDataset
 * @property {string} [blockId] - Unique block identifier for morph targeting.
 * @property {string} [thresholdCents] - Free shipping threshold in cents.
 * @property {string} [variantPriceCents] - Current variant price in cents.
 * @property {string} [availableStock] - Max addable quantity; -1 means unlimited (continue selling).
 * @property {string} [purchaseType] - Variant's purchase type from erp.purchase_type metafield (in_bond or duty_paid).
 * @property {string} [buttonTextTemplate] - Button label template containing the `__COUNT__` placeholder.
 *
 * @extends HTMLElement
 */
class FreeShippingButtonComponent extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });

    this.addEventListener('submit', this.#onFormSubmit, { signal });

    const closestSection = this.closest('.shopify-section, dialog, product-card');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * Enables or disables the loading state on the button.
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="freeShippingButton"]'));
    if (!button) return;

    if (isLoading) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    } else {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /** Starts the loading state when the form is submitted. */
  #onFormSubmit = () => {
    this.#setLoading(true);
  };

  /** Clears the loading state when a cart error occurs. */
  #onCartError = () => {
    this.#setLoading(false);
  };

  /**
   * Sets the hidden attribute on the product form component.
   * @param {boolean} hidden
   */
  #setHidden = (hidden) => {
    const productFormComponent = this.querySelector('product-form-component');
    if (productFormComponent) {
      (/** @type {HTMLElement} */ (productFormComponent)).hidden = hidden;
    }
  };

  /**
   * Recalculates button state whenever the cart changes.
   * Hides the button if the cart already qualifies for free shipping or if
   * there is insufficient stock. Otherwise updates the quantity input and label.
   * Falls back to fetching /cart.js when the event carries no cart data
   * (e.g. after a product-form-component submission).
   * @param {CustomEvent} event
   */
  #onCartUpdate = async (event) => {
    const cart = event.detail?.resource;

    if (cart && typeof cart.total_price === 'number') {
      this.#setLoading(false);
      this.#recalculate(cart);
      return;
    }

    try {
      const cartData = await fetchCart();
      this.#setLoading(false);
      this.#recalculate(cartData);
    } catch {
      console.error('Failed to fetch cart data');
      this.#setLoading(false);
      this.#setHidden(true);
    }
  };

  /**
   * Updates the button visibility, quantity input, and label based on the
   * provided cart state.
   * @param {Cart} cart
   */
  #recalculate = (cart) => {
    const thresholdCents = parseInt(this.dataset.thresholdCents || '0');
    const variantPrice = parseInt(this.dataset.variantPriceCents || '0');

    if (cart.total_price >= thresholdCents || variantPrice <= 0) {
      this.#setHidden(true);
      return;
    }

    const remaining = thresholdCents - cart.total_price;
    const floor = Math.floor(remaining / variantPrice);
    const itemsNeeded = floor * variantPrice < remaining ? floor + 1 : floor;

    if (itemsNeeded <= 0) {
      this.#setHidden(true);
      return;
    }

    const availableStock = parseInt(this.dataset.availableStock || '-1');
    const purchaseType = this.dataset.purchaseType;
    const caseSize = parseInt(this.dataset.caseSize || '1');

    if (availableStock >= 0 && availableStock < itemsNeeded) {
      this.#setHidden(true);
      return;
    }

    // Case-pack products are excluded: quantity would break case alignment
    if (purchaseType == 'in_bond' || caseSize > 1) {
      this.#setHidden(true);
      return;
    }

    const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (quantityInput) {
      quantityInput.value = String(itemsNeeded);
    }

    const textEl = this.querySelector('.free-shipping-button-text');
    const template = this.dataset.buttonTextTemplate;
    if (textEl && template) {
      textEl.textContent = template.replace('__COUNT__', String(itemsNeeded));
    }

    this.#setHidden(false);
  };

  /**
   * Replaces element content with the server-rendered version for the new
   * variant, keeping price, stock limit, and quantity in sync.
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const newHtml = event.detail?.data?.html;
    if (!newHtml) return;

    const newComponent = newHtml.querySelector(
      `free-shipping-button-component[data-block-id="${this.dataset.blockId}"]`
    );

    if (newComponent) {
      morph(this, newComponent);
    }
  };
}

if (!customElements.get('free-shipping-button-component')) {
  customElements.define('free-shipping-button-component', FreeShippingButtonComponent);
}
